/**
 * Drawn state views (backend inventory VISUALS): an item whose states are SHOWN, not described. The close-up draws
 * the thing in its state, moves it when it changes, and its parts are the controls (pull the wedge out, push the door
 * shut, hold the lift lever, pull the key). Each view:
 *   View({ it, s, g, can, act })  - `can(aid)`: the mission uses that action and it's offered now;
 *                                   `act(aid)`: do it (false when blocked - its line explains why)
 *   handles                       - the actions it draws itself (the close-up shows no other control for them)
 * Every part that's a control is a labelled button (keyboard: Enter / Space) - the state is in its accessible label.
 */
import React, { useEffect, useRef, useState } from 'react';

const WEDGES = [[/extinguisher/i, '🧯'], [/chair/i, '🪑'], [/box|carton/i, '📦'], [/bin|bucket/i, '🪣'], [/brick|block/i, '🧱']];
const wedgeOf = (text) => (WEDGES.find(([re]) => re.test(text || '')) || [null, null])[1];

/** A door in its frame - shut (latched), open, or propped open with something in the gap. */
function DoorVisual({ it, s, g, can, act }) {
  const stage = s.stage;
  const [pulling, setPulling] = useState(false);
  const open = stage === 'open' || stage === 'propped';
  const angle = stage === 'propped' ? -48 : stage === 'open' ? -70 : 0;
  const wedge = wedgeOf(it.inv.states.propped?.conveys);
  const pull = () => {
    if (!can('unwedge') || pulling) return;
    setPulling(true);
    setTimeout(() => { if (!act('unwedge')) setPulling(false); }, 450);
  };
  useEffect(() => { if (stage !== 'propped') setPulling(false); }, [stage]);
  return (
    <div className={`mg-door is-${stage}`} role="img" aria-label={`${it.name}: ${stage === 'shut' ? 'shut and latched' : stage === 'propped' ? 'propped open' : 'open'}`}>
      <div className="mg-door-frame">
        <div className="mg-door-beyond" aria-hidden="true" />
        <button type="button" className="mg-door-leaf" style={{ transform: `perspective(700px) rotateY(${angle}deg)` }}
          disabled={open ? !can('close') : !can('reopen')} aria-label={open ? 'Push the door shut' : 'Open the door'}
          onClick={() => act(open ? 'close' : 'reopen')}>
          {it.src ? <img src={g.asset(it.src)} alt="" draggable={false} /> : <span className="mg-door-plain" />}
          <span className="mg-door-handle" aria-hidden="true" />
        </button>
        <span className={`mg-door-bolt ${stage === 'shut' ? 'is-home' : ''}`} aria-hidden="true" />
        <span className={`mg-door-lamp ${stage === 'shut' ? 'is-secure' : ''}`} aria-hidden="true" />
        {stage === 'propped' && (
          <button type="button" className={`mg-door-wedge ${pulling ? 'is-pulled' : ''}`} disabled={!can('unwedge')}
            aria-label="Pull out what's holding it open" onClick={pull}>
            {wedge || <svg viewBox="0 0 60 30" aria-hidden="true"><path d="M2 28 L58 28 L58 6 Z" fill="#b98a4a" stroke="#3a2d17" strokeWidth="3" /></svg>}
          </button>
        )}
      </div>
    </div>
  );
}
DoorVisual.handles = ['close', 'unwedge', 'reopen'];

const LIFT_MS = 1500;
const LIFT = 130;                                  // how high the forks go (px in the drawing)

/** A forklift from the side - forks raised or down, the key in the ignition or out. Hold the lift lever and the forks
 * move while you hold (down when they're up; up again when the key's in); pull the key out, or put it back. */
function ForkliftVisual({ it, s, can, act }) {
  const stage = s.stage;
  const [moved, setMoved] = useState(0);            // 0..1 of the way while the lever's held
  const [holding, setHolding] = useState(false);
  const [keyOut, setKeyOut] = useState(false);
  const [keyBack, setKeyBack] = useState(false);
  const timer = useRef(null);
  const raised = stage === 'forks_up';
  const keyIn = stage !== 'safe';
  const way = raised && can('lower') ? 'lower' : stage === 'keys_in' && can('raise') ? 'raise' : null;
  const forkY = raised ? -LIFT * (1 - (way === 'lower' ? moved : 0)) : way === 'raise' ? -LIFT * moved : 0;
  const stop = () => { clearInterval(timer.current); timer.current = null; setHolding(false); };
  const hold = () => {
    if (!way || timer.current) return;
    const began = Date.now() - moved * LIFT_MS;
    setHolding(true);
    timer.current = setInterval(() => {
      const f = Math.min(1, (Date.now() - began) / LIFT_MS);
      setMoved(f);
      if (f >= 1) { stop(); act(way); }
    }, 40);
  };
  const pullKey = () => {
    if (!keyIn || keyOut) return;
    if (!can('take_keys')) { act('take_keys'); return; }      // not now (forks still up): its line says why
    setKeyOut(true);
    setTimeout(() => { if (!act('take_keys')) setKeyOut(false); }, 550);
  };
  const putBack = () => { if (act('keys_back')) setKeyBack(true); };
  useEffect(() => stop, []);
  useEffect(() => { setMoved(0); if (stage !== 'safe') setKeyOut(false); }, [stage]);
  const lever = way === 'lower' ? -28 * moved : way === 'raise' ? 28 * moved : 0;
  const label = `${it.name}: forks ${raised ? 'raised' : 'down'}, key ${keyIn ? 'in the ignition' : 'out'}`;
  return (
    <div className={`mg-forklift is-${stage}`}>
      <svg viewBox="0 0 430 290" role="img" aria-label={label}>
        <line x1="10" y1="262" x2="420" y2="262" stroke="#3a2d17" strokeWidth="3" />
        {/* mast and the forks on their carriage */}
        <rect x="296" y="40" width="12" height="214" fill="#7a5a2e" stroke="#3a2d17" strokeWidth="3" />
        <rect x="312" y="40" width="8" height="214" fill="#8d6a38" stroke="#3a2d17" strokeWidth="2" />
        <g className={`mg-fork-carriage ${holding ? 'is-moving' : ''}`} style={{ transform: `translateY(${forkY}px)` }}>
          <rect x="320" y="196" width="16" height="58" fill="#c89b5a" stroke="#3a2d17" strokeWidth="3" />
          <rect x="320" y="246" width="100" height="9" fill="#9a7440" stroke="#3a2d17" strokeWidth="3" />
          {raised && <path d="M370 236 l10 -14 l10 14 z" fill="#e0a030" stroke="#3a2d17" strokeWidth="2" className="mg-fork-warning" />}
        </g>
        {/* body, counterweight, cab */}
        <path d="M30 205 Q30 165 70 160 L290 160 L290 238 L40 238 Q30 238 30 225 Z" fill="#c89b5a" stroke="#3a2d17" strokeWidth="4" />
        <rect x="30" y="170" width="44" height="66" rx="8" fill="#a67b42" stroke="#3a2d17" strokeWidth="3" />
        <line x1="110" y1="160" x2="122" y2="52" stroke="#3a2d17" strokeWidth="8" strokeLinecap="round" />
        <line x1="262" y1="160" x2="252" y2="52" stroke="#3a2d17" strokeWidth="8" strokeLinecap="round" />
        <rect x="104" y="44" width="160" height="12" rx="3" fill="#7a5a2e" stroke="#3a2d17" strokeWidth="3" />
        <path d="M128 160 L128 118 Q128 106 140 106 L150 106 L150 160 Z" fill="#5c4424" stroke="#3a2d17" strokeWidth="3" />
        <rect x="140" y="140" width="46" height="20" rx="4" fill="#5c4424" stroke="#3a2d17" strokeWidth="3" />
        <line x1="236" y1="160" x2="222" y2="112" stroke="#3a2d17" strokeWidth="5" />
        <ellipse cx="220" cy="108" rx="18" ry="6" fill="none" stroke="#3a2d17" strokeWidth="5" />
        {/* dashboard with the ignition */}
        <rect x="240" y="118" width="40" height="42" rx="4" fill="#8d6a38" stroke="#3a2d17" strokeWidth="3" />
        <circle cx="260" cy="134" r="7" fill="#1e1a15" />
        <circle cx="272" cy="124" r="4" fill={keyIn ? '#8cff9a' : '#3a2d17'} className="mg-fork-on" />
        {keyIn && (
          <g className={`mg-fork-key ${keyOut ? 'is-out' : ''} ${keyBack ? 'is-back' : ''} ${can('take_keys') ? 'is-ready' : ''}`} role="button" tabIndex={0}
            aria-label="Pull the key out" onClick={pullKey} onAnimationEnd={() => setKeyBack(false)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pullKey(); } }}>
            <rect x="256" y="112" width="8" height="22" rx="2" fill="#e9c97a" stroke="#3a2d17" strokeWidth="2" />
            <circle cx="260" cy="108" r="8" fill="#e9c97a" stroke="#3a2d17" strokeWidth="2" />
            <g className="mg-fork-tag"><line x1="260" y1="134" x2="268" y2="150" stroke="#3a2d17" strokeWidth="2" />
              <rect x="262" y="148" width="14" height="10" rx="2" fill="#b5432f" stroke="#3a2d17" strokeWidth="1.5" /></g>
            <circle cx="260" cy="122" r="22" fill="transparent" />
          </g>
        )}
        {/* the lift lever: hold it to move the forks */}
        <g className={`mg-fork-lever ${holding ? 'is-held' : ''} ${way ? 'is-ready' : ''}`} style={{ transform: `rotate(${lever}deg)` }}
          role="button" tabIndex={way ? 0 : -1} aria-disabled={!way}
          aria-label={way === 'raise' ? 'Hold the lift lever to raise the forks' : 'Hold the lift lever to lower the forks'}
          onPointerDown={hold} onPointerUp={stop} onPointerLeave={stop}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && way) { e.preventDefault(); act(way); } }}>
          <line x1="200" y1="160" x2="206" y2="118" stroke="#3a2d17" strokeWidth="5" strokeLinecap="round" />
          <circle cx="206" cy="116" r="8" fill="#b5432f" stroke="#3a2d17" strokeWidth="2" />
          <circle cx="204" cy="132" r="26" fill="transparent" />
        </g>
        {/* wheels */}
        <circle cx="96" cy="236" r="26" fill="#2b2216" stroke="#3a2d17" strokeWidth="3" /><circle cx="96" cy="236" r="9" fill="#c89b5a" />
        <circle cx="250" cy="234" r="28" fill="#2b2216" stroke="#3a2d17" strokeWidth="3" /><circle cx="250" cy="234" r="10" fill="#c89b5a" />
      </svg>
      {way && (
        <div className="mg-fork-meter" aria-hidden="true"><i style={{ width: `${moved * 100}%` }} /></div>
      )}
      {!keyIn && can('keys_back') && (
        <button type="button" className="mg-fork-keytoken" aria-label="Put the key back in" onClick={putBack}>🔑</button>
      )}
    </div>
  );
}
ForkliftVisual.handles = ['lower', 'take_keys', 'raise', 'keys_back'];

const num = (v) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : null);

/** A gauge (thermometer, pressure): the needle settles on the reading, the safe band is green, outside it red.
 * The reading is read off the dial - it's never written. */
function GaugeVisual({ it }) {
  const t = it.inv.text || {};
  const reading = num(t.reading) ?? 0;
  const low = num(t.low);
  const high = num(t.high);
  const span = [reading, low, high].filter((v) => v !== null);
  const pad = Math.max(2, (Math.max(...span) - Math.min(...span)) * 0.6);
  const lo = Math.floor(Math.min(...span) - pad);
  const hi = Math.ceil(Math.max(...span) + pad);
  const ang = (v) => -120 + (240 * (v - lo)) / Math.max(1, hi - lo);
  const pt = (deg, r) => [100 + r * Math.sin((deg * Math.PI) / 180), 100 - r * Math.cos((deg * Math.PI) / 180)];
  const arc = (from, to, r) => {
    const [x1, y1] = pt(ang(from), r);
    const [x2, y2] = pt(ang(to), r);
    return `M${x1} ${y1} A${r} ${r} 0 ${ang(to) - ang(from) > 180 ? 1 : 0} 1 ${x2} ${y2}`;
  };
  const [shown, setShown] = useState(lo);
  useEffect(() => { const k = setTimeout(() => setShown(reading), 120); return () => clearTimeout(k); }, [reading]);
  const ticks = Array.from({ length: 6 }, (_, i) => Math.round(lo + ((hi - lo) * i) / 5));
  const outside = (low !== null && reading < low) || (high !== null && reading > high);
  return (
    <div className="mg-gauge">
      <svg viewBox="0 0 200 170" role="img" aria-label={`${t.what || it.name}: ${reading} ${t.unit || ''}${outside ? ', outside the safe range' : ''}`}>
        <circle cx="100" cy="100" r="92" fill="#fdf6e3" stroke="#7a5a2e" strokeWidth="8" />
        {low !== null && <path d={arc(lo, low, 76)} stroke="#d9534f" strokeWidth="10" fill="none" />}
        <path d={arc(low ?? lo, high ?? hi, 76)} stroke="#5bb46a" strokeWidth="10" fill="none" />
        {high !== null && <path d={arc(high, hi, 76)} stroke="#d9534f" strokeWidth="10" fill="none" />}
        {ticks.map((v) => {
          const [x1, y1] = pt(ang(v), 70);
          const [x2, y2] = pt(ang(v), 60);
          const [tx, ty] = pt(ang(v), 47);
          return (
            <g key={v}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#3a2d17" strokeWidth="2" />
              <text x={tx} y={ty + 4} textAnchor="middle" fontSize="11" fill="#3a2d17">{v}</text>
            </g>
          );
        })}
        <text x="100" y="140" textAnchor="middle" fontSize="12" fill="#3a2d17">{t.unit || ''}</text>
        <g className="mg-gauge-needle" style={{ transform: `rotate(${ang(shown)}deg)` }}>
          <line x1="100" y1="100" x2="100" y2="34" stroke="#b5432f" strokeWidth="4" strokeLinecap="round" />
        </g>
        <circle cx="100" cy="100" r="7" fill="#1e1a15" />
      </svg>
      {t.what && <span className="mg-gauge-plate">{t.what}</span>}
    </div>
  );
}

/** A fridge: the door stands open (and the alarm beeps) or it's shut; the display shows the temperature it's set to. */
function FridgeVisual({ it, s, can, act }) {
  const open = s.stage === 'open';
  const set = s.temp;
  const unit = it.inv.actions.set_temp?.unit || '°C';
  return (
    <div className={`mg-fridge ${open ? 'is-open' : 'is-shut'}`} role="img" aria-label={`${it.name}: door ${open ? 'open, alarm beeping' : 'shut'}, set to ${set ?? '–'} ${unit}`}>
      <div className="mg-fridge-display" aria-hidden="true">{set ?? '--'}{unit}{open && <span className="mg-fridge-alarm">🔔</span>}</div>
      <div className="mg-fridge-body">
        <div className="mg-fridge-inside" aria-hidden="true"><i /><i /><i /></div>
        <button type="button" className="mg-fridge-door" style={{ transform: `perspective(700px) rotateY(${open ? -75 : 0}deg)` }}
          disabled={open ? !can('close') : !can('reopen')} aria-label={open ? 'Shut the fridge door' : 'Open the fridge door'}
          onClick={() => act(open ? 'close' : 'reopen')}>
          <span className="mg-fridge-handle" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
FridgeVisual.handles = ['close', 'reopen'];

/** A food probe: tap it into the food; the readout climbs and settles on the core temperature, with the safe line. */
function ProbeVisual({ it, s, can, act }) {
  const t = it.inv.text || {};
  const reading = num(t.reading) ?? 0;
  const safe = num(t.safe);
  const inserted = s.stage === 'probed';
  const [shown, setShown] = useState(inserted ? reading : null);
  useEffect(() => {
    if (!inserted) { setShown(null); return undefined; }
    let v = 20;
    setShown(v);
    const k = setInterval(() => { v = Math.min(reading, v + Math.max(1, Math.round((reading - v) / 4))); setShown(v); if (v >= reading) clearInterval(k); }, 120);
    return () => clearInterval(k);
  }, [inserted, reading]);
  const low = safe !== null && shown !== null && shown < safe;
  return (
    <div className={`mg-probe ${inserted ? 'is-in' : ''}`}>
      <svg viewBox="0 0 300 170" role="img" aria-label={`${t.food || it.name}${inserted ? `: ${reading} ${t.unit || ''}` : ''}`}>
        <rect x="10" y="120" width="280" height="40" rx="6" fill="#c89b5a" stroke="#3a2d17" strokeWidth="3" />
        <ellipse cx="120" cy="112" rx="80" ry="30" fill="#d9a15a" stroke="#3a2d17" strokeWidth="3" />
        <ellipse cx="105" cy="104" rx="40" ry="10" fill="#e8bd7a" opacity="0.7" />
        <g className="mg-probe-spike" style={{ transform: inserted ? 'translate(-70px, 58px) rotate(-30deg)' : 'none' }}>
          <line x1="230" y1="40" x2="290" y2="10" stroke="#9aa0a6" strokeWidth="5" strokeLinecap="round" />
          <rect x="196" y="36" width="46" height="26" rx="5" fill="#2b2b2b" stroke="#111" strokeWidth="2" transform="rotate(-27 219 49)" />
        </g>
      </svg>
      <div className={`mg-probe-readout ${low ? 'is-low' : shown !== null ? 'is-ok' : ''}`} aria-live="polite">
        {shown === null ? '--' : shown}{t.unit || ''}{safe !== null && <small> / {safe}{t.unit || ''}</small>}
      </div>
      {!inserted && can('probe') && <button type="button" className="mg-btn" onClick={() => act('probe')}>Push the probe in</button>}
      {inserted && can('pull_out') && <button type="button" className="mg-btn mg-btn-quiet" onClick={() => act('pull_out')}>Pull the probe out</button>}
    </div>
  );
}
ProbeVisual.handles = ['probe', 'pull_out'];

/** A patient monitor: heart rate with its trace, blood pressure, oxygen - each red when it's outside its normal band. */
function VitalsVisual({ it }) {
  const t = it.inv.text || {};
  const hr = num(t.hr);
  const [sys, dia] = String(t.bp || '').split('/').map(num);
  const spo2 = num(t.spo2);
  const bad = { hr: hr !== null && (hr < 60 || hr > 100), bp: (sys !== null && (sys < 90 || sys > 140)) || (dia !== null && (dia < 60 || dia > 90)),
    spo2: spo2 !== null && spo2 < 95 };
  return (
    <div className="mg-vitals" role="img" aria-label={`${t.patient || it.name}: heart rate ${t.hr}, blood pressure ${t.bp}, oxygen ${t.spo2}%`}>
      {t.patient && <span className="mg-vitals-who">{t.patient}</span>}
      <svg viewBox="0 0 300 50" className="mg-vitals-trace" aria-hidden="true">
        <path d="M0 30 L40 30 L48 10 L56 46 L64 30 L110 30 L118 10 L126 46 L134 30 L180 30 L188 10 L196 46 L204 30 L250 30 L258 10 L266 46 L274 30 L300 30"
          fill="none" stroke="#5bd46a" strokeWidth="2.5" />
      </svg>
      <div className="mg-vitals-row">
        <span className={bad.hr ? 'is-alarm' : ''}><small>HR</small>{t.hr}</span>
        <span className={bad.bp ? 'is-alarm' : ''}><small>BP</small>{t.bp}</span>
        <span className={bad.spo2 ? 'is-alarm' : ''}><small>SpO₂</small>{t.spo2}%</span>
      </div>
    </div>
  );
}
VitalsVisual.handles = [];

const ROLL_MS = 1500;

/** A roller door: hold to bring it down (or up); lock it with the padlock at the bottom. */
function RollerDoorVisual({ it, s, can, act }) {
  const stage = s.stage;
  const [moved, setMoved] = useState(0);
  const timer = useRef(null);
  const way = stage === 'up' && can('lower') ? 'lower' : stage === 'down' && can('raise') ? 'raise' : null;
  const open = stage === 'up' ? 1 - (way === 'lower' ? moved : 0) : way === 'raise' ? moved : 0;
  const stop = () => { clearInterval(timer.current); timer.current = null; };
  const hold = () => {
    if (!way || timer.current) return;
    const began = Date.now() - moved * ROLL_MS;
    timer.current = setInterval(() => { const f = Math.min(1, (Date.now() - began) / ROLL_MS); setMoved(f); if (f >= 1) { stop(); act(way); } }, 40);
  };
  useEffect(() => stop, []);
  useEffect(() => { setMoved(0); }, [stage]);
  const locked = stage === 'locked';
  return (
    <div className={`mg-roller is-${stage}`}>
      <div className="mg-roller-frame" role="img" aria-label={`${it.name}: ${locked ? 'down and locked' : stage === 'up' ? 'up' : 'down'}`}>
        <div className="mg-roller-beyond" aria-hidden="true" />
        <div className="mg-roller-shutter" style={{ height: `${(1 - open) * 100}%` }} aria-hidden="true" />
        {(stage === 'down' || locked) && (
          <button type="button" className={`mg-roller-lock ${locked ? 'is-locked' : ''}`} disabled={locked ? !can('unlock') : !can('lock')}
            aria-label={locked ? 'Unlock the door' : 'Lock the door'} onClick={() => act(locked ? 'unlock' : 'lock')}>{locked ? '🔒' : '🔓'}</button>
        )}
      </div>
      {way && (
        <button type="button" className="mg-btn mg-hold-btn" onPointerDown={hold} onPointerUp={stop} onPointerLeave={stop}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); act(way); } }}>
          <i style={{ width: `${moved * 100}%` }} aria-hidden="true" /><span>{way === 'lower' ? 'Hold to lower' : 'Hold to raise'}</span>
        </button>
      )}
    </div>
  );
}
RollerDoorVisual.handles = ['lower', 'raise', 'lock', 'unlock'];

const ROOMS = ['Office', 'Kitchen', 'Store room', 'Workshop'];
const SPOT_ICON = { 'Exit A': '🚪', 'Exit B': '🚪', 'Exit C': '🚪', Stairs: '🪜', Lift: '🛗' };

/** A floor plan: rooms and corridors drawn from its spots; you are here (📍), what's blocked (🔥); tap where to go. */
function FloorPlanVisual({ it, s, can, act }) {
  const spots = it.inv.hotspots || {};
  const marks = it.inv.marks || {};
  const went = s.went;
  return (
    <div className="mg-plan" role="group" aria-label={it.name}>
      <div className="mg-plan-map">
        <div className="mg-plan-corridor" aria-hidden="true" />
        {Object.entries(spots).map(([name, [x, y, w, h]]) => {
          const room = ROOMS.includes(name);
          const style = { left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` };
          const mark = marks[name];
          const inner = (
            <>
              <span className="mg-plan-name">{room ? name : SPOT_ICON[name] || name}</span>
              {!room && <small>{name}</small>}
              {mark && <span className={`mg-plan-mark is-${mark}`} aria-label={mark === 'here' ? 'You are here' : 'Blocked'}>{mark === 'here' ? '📍' : '🔥'}</span>}
            </>
          );
          return can('go') && !room ? (
            <button key={name} type="button" className={`mg-plan-spot is-exit ${went === name ? 'is-picked' : ''}`} style={style}
              aria-label={`Go to ${name}${mark === 'fire' ? ' (blocked)' : ''}`} onClick={() => act('go', name)}>{inner}</button>
          ) : (
            <div key={name} className={`mg-plan-spot ${room ? 'is-room' : 'is-exit'}`} style={style}>{inner}</div>
          );
        })}
      </div>
    </div>
  );
}
FloorPlanVisual.handles = ['go'];

/** A secure cage: the stock on its shelves (to count), the mesh door and its padlock - tap the padlock to lock it. */
function CageVisual({ it, s, can, act }) {
  const pile = it.inv.states?.[s.stage]?.pile ?? it.inv.states?.unlocked?.pile ?? 0;
  const locked = s.stage === 'locked';
  return (
    <div className={`mg-cage ${locked ? 'is-locked' : ''}`}>
      <div className="mg-cage-box" role="img" aria-label={`${it.name}: ${locked ? 'locked' : 'unlocked'}`}>
        <div className="mg-cage-stock" aria-hidden="true">{Array.from({ length: pile }, (_, i) => <span key={i}>{it.inv.pile_icon || '📦'}</span>)}</div>
        <div className="mg-cage-mesh" aria-hidden="true" />
      </div>
      <button type="button" className={`mg-cage-lock ${locked ? 'is-locked' : ''}`} disabled={locked ? !can('unlock') : !can('lock')}
        aria-label={locked ? 'Unlock the cage' : 'Lock the cage'} onClick={() => act(locked ? 'unlock' : 'lock')}>{locked ? '🔒' : '🔓'}</button>
    </div>
  );
}
CageVisual.handles = ['lock', 'unlock'];

/** A key cabinet: tap its door to open it; inside, rows of hooks. */
function KeyCabinetVisual({ it, s, can, act }) {
  const open = s.stage === 'open';
  return (
    <div className={`mg-keycab ${open ? 'is-open' : ''}`}>
      <div className="mg-keycab-body" role="img" aria-label={`${it.name}: ${open ? 'open' : 'shut'}`}>
        <div className="mg-keycab-hooks" aria-hidden="true">{Array.from({ length: 8 }, (_, i) => <i key={i} />)}</div>
        <button type="button" className="mg-keycab-door" style={{ transform: `perspective(700px) rotateY(${open ? -80 : 0}deg)` }}
          disabled={open ? !can('close') : !can('open')} aria-label={open ? 'Close the key cabinet' : 'Open the key cabinet'}
          onClick={() => act(open ? 'close' : 'open')} />
      </div>
    </div>
  );
}
KeyCabinetVisual.handles = ['open', 'close'];

/** An alarm panel: its light (red until armed), its zones (red while one is still open) and the ARM button. */
function AlarmVisual({ it, s, g, can, act }) {
  const armed = s.stage === 'armed';
  const guarded = (it.inv.watches || []).map((id) => g.byId[id]).filter(Boolean);
  const startOfIt = (x) => x.inv?.start ?? Object.keys(x.inv?.states || {})[0];
  return (
    <div className={`mg-alarm ${armed ? 'is-armed' : ''}`}>
      <div className="mg-alarm-head">
        <i className="mg-alarm-led" aria-hidden="true" />
        <span className="mg-alarm-display" aria-live="polite">{armed ? 'ARMED' : s.code ? '****' : '----'}</span>
      </div>
      {guarded.length > 0 && (
        <ul className="mg-zones" aria-label="Zones">
          {guarded.map((x, i) => {
            const zoneOpen = (g.ctx.state[x.id] || {}).stage === startOfIt(x);
            return <li key={x.id} className={zoneOpen ? 'is-open' : 'is-secure'}><i aria-hidden="true" /> Zone {i + 1} · {x.name} — {zoneOpen ? 'open' : 'secure'}</li>;
          })}
        </ul>
      )}
      {!armed && it.inv.actions.arm && (
        <button type="button" className="mg-btn mg-alarm-arm" disabled={!can('arm')} onClick={() => act('arm')}>ARM</button>
      )}
      {armed && can('disarm') && (
        <button type="button" className="mg-btn mg-btn-quiet mg-alarm-arm" onClick={() => act('disarm')}>DISARM</button>
      )}
    </div>
  );
}
AlarmVisual.handles = ['arm', 'disarm'];

const COFFEE_ORDER = ['ready', 'ground', 'tamped', 'locked', 'brewed'];
const GRIND_MS = 1500;

/** An espresso machine worked by its parts, in order: hold to grind (the grounds fill the handle), tamp them flat,
 * lock the handle into the machine, hold to brew (the shot pours into the cup). Out of order, it says what comes first. */
function CoffeeVisual({ it, s, can, act }) {
  const stage = s.stage;
  const at = COFFEE_ORDER.indexOf(stage);
  const [held, setHeld] = useState({ aid: null, f: 0 });
  const timer = useRef(null);
  const stop = () => { clearInterval(timer.current); timer.current = null; setHeld({ aid: null, f: 0 }); };
  const hold = (aid) => {
    if (timer.current) return;
    if (!can(aid)) { act(aid); return; }                    // not now: its line says what comes first
    const began = Date.now();
    timer.current = setInterval(() => {
      const f = Math.min(1, (Date.now() - began) / GRIND_MS);
      setHeld({ aid, f });
      if (f >= 1) { clearInterval(timer.current); timer.current = null; setHeld({ aid: null, f: 0 }); act(aid); }
    }, 40);
  };
  useEffect(() => () => clearInterval(timer.current), []);
  const grounds = at >= 1 ? 1 : held.aid === 'grind' ? held.f : 0;
  const shot = at >= 4 ? 1 : held.aid === 'brew' ? held.f : 0;
  const locked = at >= 3;
  const holdBtn = (aid, label) => (
    <button type="button" className={`mg-btn mg-hold-btn ${can(aid) ? 'is-ready' : 'is-later'}`} onPointerDown={() => hold(aid)} onPointerUp={stop}
      onPointerLeave={stop} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); act(aid); } }}>
      <i style={{ width: `${held.aid === aid ? held.f * 100 : 0}%` }} aria-hidden="true" /><span>{label}</span>
    </button>
  );
  const tapBtn = (aid, label) => (
    <button type="button" className={`mg-btn ${can(aid) ? 'is-ready' : 'is-later'}`} onClick={() => act(aid)}>{label}</button>
  );
  const said = ['ready to grind', 'ground, not tamped', 'tamped', 'handle locked in', 'shot brewed'][at] || stage;
  return (
    <div className={`mg-coffee is-${stage}`}>
      <svg viewBox="0 0 320 230" role="img" aria-label={`${it.name}: ${said}`}>
        {/* grinder */}
        <path d="M18 20 L62 20 L54 70 L26 70 Z" fill="#d9c9a0" stroke="#3a2d17" strokeWidth="3" />
        <rect x="24" y="70" width="32" height="50" rx="4" fill="#5c5a55" stroke="#3a2d17" strokeWidth="3" />
        {held.aid === 'grind' && <path d="M40 120 L40 150" stroke="#5a3a1a" strokeWidth="6" strokeDasharray="3 4" className="mg-coffee-stream" />}
        {/* machine */}
        <rect x="90" y="20" width="200" height="150" rx="10" fill="url(#mg-steel)" stroke="#3a2d17" strokeWidth="4" />
        <defs>
          <linearGradient id="mg-steel" x1="0" x2="1"><stop offset="0" stopColor="#9aa0a6" /><stop offset="0.5" stopColor="#d8dcdf" /><stop offset="1" stopColor="#8b9298" /></linearGradient>
        </defs>
        <rect x="160" y="70" width="60" height="22" rx="4" fill="#5c6166" stroke="#3a2d17" strokeWidth="3" />
        <circle cx="255" cy="50" r="12" fill={at >= 3 ? '#5bd46a' : '#8b9298'} stroke="#3a2d17" strokeWidth="3" />
        <rect x="100" y="175" width="180" height="12" rx="3" fill="#5c6166" stroke="#3a2d17" strokeWidth="2" />
        {/* the handle (portafilter): on the counter until it's locked in under the group head */}
        <g className="mg-coffee-handle" style={{ transform: locked ? 'translate(118px, -95px)' : 'none' }}>
          <path d="M40 190 Q42 210 72 210 Q102 210 104 190 Z" fill="#3a3a3a" stroke="#1e1e1e" strokeWidth="3" />
          <rect x="0" y="194" width="40" height="10" rx="5" fill="#1e1a15" />
          {grounds > 0 && (at >= 2
            ? <rect x="46" y="186" width="52" height="6" fill="#5a3a1a" />
            : <ellipse cx="72" cy={190 - 8 * grounds} rx={26 * Math.max(0.3, grounds)} ry={8 * grounds} fill="#6b4422" />)}
        </g>
        {/* the cup under the group head */}
        <path d="M168 140 L172 172 L208 172 L212 140 Z" fill="#fdf6e3" stroke="#3a2d17" strokeWidth="3" />
        <rect x="172" y={172 - 28 * shot} width="36" height={28 * shot} fill="#4a2a10" opacity="0.9" />
        {held.aid === 'brew' && <path d="M190 95 L190 140" stroke="#4a2a10" strokeWidth="4" className="mg-coffee-stream" />}
      </svg>
      <div className="mg-coffee-controls">
        {holdBtn('grind', 'Hold to grind')}
        {tapBtn('tamp', 'Tamp')}
        {tapBtn('lock', 'Lock the handle in')}
        {holdBtn('brew', 'Hold to brew')}
      </div>
    </div>
  );
}
CoffeeVisual.handles = ['grind', 'tamp', 'lock', 'brew'];

export const VISUALS = { door: DoorVisual, forklift: ForkliftVisual, gauge: GaugeVisual, fridge: FridgeVisual, coffee: CoffeeVisual, probe: ProbeVisual,
  vitals: VitalsVisual, roller_door: RollerDoorVisual, floor_plan: FloorPlanVisual, cage: CageVisual, key_cabinet: KeyCabinetVisual,
  alarm: AlarmVisual };
